import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, BadgeCheck, Wallet, BedDouble, ArrowRight, Search, CalendarDays, Users } from "lucide-react";
import { peso } from "@/lib/api";
import { propertyTypeLabel } from "@/lib/taxonomy";
import { proProfilePath, trackProfileClick } from "@/lib/proProfile";

const GENERIC_TITLE = /^(anon(ymous)?|anonymous buyer|anon buyer|buyer|unnamed|untitled|n\/?a|tbd)$/i;

const budgetLabel = (b) => {
  if (b.listing_type === "Unit Hosting") {
    const lo = b.nightly_budget_min || 0, hi = b.nightly_budget_max || 0;
    if (lo && hi) return `${peso(lo)} – ${peso(hi)} / night`;
    if (hi) return `Up to ${peso(hi)} / night`;
    if (lo) return `${peso(lo)}+ / night`;
    return b.budget_soft ? "Budget flexible" : "Not specified";
  }
  const lo = b.budget_min || 0, hi = b.budget_max || 0;
  if (lo && hi) return `${peso(lo)} – ${peso(hi)}`;
  if (hi) return `Up to ${peso(hi)}`;
  if (lo) return `${peso(lo)}+`;
  return b.budget_soft ? "Budget flexible" : "Not specified";
};

// Freshness shown ONCE per card with an accurate Posted vs Updated label.
const freshnessLabel = (n) => {
  const u = n.updated_at, c = n.created_at;
  const ts = u || c;
  if (!ts) return "";
  const edited = u && c && (new Date(u).getTime() - new Date(c).getTime() > 120000);
  const verb = edited ? "Updated" : "Posted";
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  const rel = d <= 0 ? "today" : d === 1 ? "yesterday" : d < 30 ? `${d} days ago` : `${Math.floor(d / 30)} mo ago`;
  return `${verb} ${rel}`;
};

// Deterministic, privacy-safe requirement title from recorded fields only.
// e.g. "2BR Condominium in Makati". Omits bedrooms/location when missing; never invents.
function requirementTitle(n, isStay) {
  const raw = (n.title || n.buyer_alias || "").trim();
  if (raw && !GENERIC_TITLE.test(raw)) return raw;
  const beds = !isStay
    ? (n.min_bedrooms > 0 ? `${n.min_bedrooms}BR ` : "")
    : (n.stay_beds_min > 0 ? `${n.stay_beds_min}BR ` : "");
  const noun = propertyTypeLabel(n.property_type) || "Property";
  const city = (n.cities || [])[0];
  let t = `${beds}${noun}`.trim();
  if (city) t += ` in ${city}`;
  return t;
}

export const BuyerNeedCard = ({ n }) => {
  const nav = useNavigate();
  const isStay = n.listing_type === "Unit Hosting";
  const location = (n.cities || []).slice(0, 2).join(", ") || "Not specified";
  const keyReqs = [...(n.required_features || []), ...(n.preferred_features || []), ...(n.must_haves || [])]
    .filter((v, i, a) => v && a.indexOf(v) === i).slice(0, 4);
  const owner = n.owner || {};
  const title = requirementTitle(n, isStay);
  // Single intent badge communicates the transaction; the old "Buyer Looking For" badge is removed.
  const intentLabel = isStay ? "Looking for a Stay" : n.listing_type === "For Sale" ? "Looking to Buy" : "Looking to Rent";
  const intentClass = n.listing_type === "For Sale"
    ? "bg-[#E9D8A6] text-[#17365D] border-[#D9C27E]"
    : "bg-[#D2E5F8] text-[#17365D] border-[#BBD4F0]";
  const fresh = freshnessLabel(n);
  return (
    <Link
      to={`/buyer-need/${n.id}`}
      data-testid={`buyer-need-card-${n.id}`}
      className="group relative isolate flex flex-col overflow-hidden rounded-2xl border border-[#DCE5EF] bg-white shadow-[0_1px_3px_rgba(23,54,93,0.08)] transition-all duration-300 hover:border-[#B9CFE6] hover:-translate-y-0.5 hover:shadow-[0_16px_38px_-16px_rgba(23,54,93,0.28)]"
    >
      {/* ===== LIGHT-BLUE HEADER (compact) ===== */}
      <div className="bg-gradient-to-br from-[#EAF4FF] to-[#D6E9FA] px-5 pt-3.5 pb-3.5">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border shrink-0 ${intentClass}`}>
            <Search className="h-3 w-3" /> {intentLabel}
          </span>
          {fresh && <span data-testid={`bn-freshness-${n.id}`} className="text-[11px] text-[#17365D]/55 shrink-0">{fresh}</span>}
        </div>
        <h3 data-testid={`bn-title-${n.id}`} className="font-display text-base font-bold text-[#17365D] leading-snug mt-2 line-clamp-2">{title}</h3>
      </div>
      {/* muted gold divider separating header from the white body */}
      <div className="h-[2px] w-full bg-[#C6A04B]" aria-hidden="true" />

      {/* ===== WHITE BODY ===== */}
      <div className="p-5 flex-1 bg-white">
        <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /> {location}</p>
        <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{propertyTypeLabel(n.property_type) || "Any type"}</p>

        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
          <p className="text-xl font-bold text-[#17365D] flex items-center gap-1.5 leading-tight"><Wallet className="h-[18px] w-[18px] text-[#C6A04B] shrink-0" /> {budgetLabel(n)}</p>
          {isStay ? (
            <>
              {n.stay_guests > 0 && <p className="text-xs text-slate-600 flex items-center gap-1.5" data-testid={`bn-guests-${n.id}`}><Users className="h-3.5 w-3.5" /> {n.stay_guests} guest(s){n.stay_beds_min > 0 ? ` · ${n.stay_beds_min}+ bed(s)` : ""}</p>}
              {(n.stay_check_in || n.stay_check_out) && <p className="text-xs text-slate-600 flex items-center gap-1.5" data-testid={`bn-dates-${n.id}`}><CalendarDays className="h-3.5 w-3.5" /> {n.stay_check_in || "?"} → {n.stay_check_out || "?"}</p>}
            </>
          ) : (
            n.min_bedrooms > 0 && <p className="text-xs text-slate-600 flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5" /> {n.min_bedrooms}+ bedroom(s)</p>
          )}
        </div>

        {keyReqs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {keyReqs.map((r) => <span key={r} className="text-[11px] font-medium bg-[#EDF3FA] text-[#17365D] border border-[#DCE5EF] px-2 py-0.5 rounded-full">{r}</span>)}
          </div>
        )}
        {n.public_description && <p className="text-xs text-slate-500 mt-3 line-clamp-2">{n.public_description}</p>}
      </div>

      {/* ===== PROFESSIONAL / ACTION FOOTER (very pale blue) ===== */}
      <div className="bg-[#F2F8FE] px-5 py-3 border-t border-[#DCE5EF]">
        <div className="flex items-center justify-between gap-2">
          {(() => {
            const path = proProfilePath(owner);
            const idBody = (
              <>
                {owner.avatar ? <img src={owner.avatar} alt="" className="h-6 w-6 rounded-full bg-[#17365D]/10 shrink-0" /> : <div className="h-6 w-6 rounded-full bg-[#17365D]/10 shrink-0" />}
                <span className={`text-xs truncate ${path ? "text-[#17365D] group-hover/pro:underline" : "text-[#17365D]/90"}`}>{owner.name}</span>
                {owner.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-3.5 w-3.5 text-[#16A34A] shrink-0" title="Verified professional" />}
              </>
            );
            return path ? (
              <button
                type="button"
                data-testid={`buyer-need-pro-link-${n.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); trackProfileClick(owner, "buyer_need_card"); nav(path); }}
                aria-label={`View ${owner.name}'s professional profile`}
                className="group/pro flex items-center gap-2 min-w-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#17365D]/40"
              >
                {idBody}
              </button>
            ) : (
              <div className="flex items-center gap-2 min-w-0" data-testid={`buyer-need-pro-static-${n.id}`}>{idBody}</div>
            );
          })()}
        </div>
        <div className="mt-2.5 pt-2.5 border-t border-[#DCE5EF]">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#17365D] bg-[#D8EBFC] border border-[#BBD4F0] rounded-full px-3.5 py-1.5 group-hover:gap-2.5 group-hover:bg-[#C8E1FA] transition-all">
            I Have a Matching Property <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};
